// Character generation utilities

// Names database
const names = {
  male: ['James', 'Michael', 'David', 'John', 'Robert', 'Christopher', 'Daniel', 'Matthew', 'Andrew', 'Thomas', 
         'Anthony', 'Richard', 'Joshua', 'William', 'Charles', 'Joseph', 'Kevin', 'Brian', 'Jason', 'Steven',
         'Liam', 'Noah', 'Oliver', 'Elijah', 'Lucas', 'Mason', 'Logan', 'Ethan', 'Jacob', 'Jackson'],
  female: ['Mary', 'Patricia', 'Jennifer', 'Linda', 'Elizabeth', 'Barbara', 'Susan', 'Jessica', 'Sarah', 'Karen',
          'Lisa', 'Nancy', 'Betty', 'Sandra', 'Margaret', 'Ashley', 'Kimberly', 'Emily', 'Donna', 'Michelle',
          'Emma', 'Olivia', 'Ava', 'Isabella', 'Sophia', 'Charlotte', 'Mia', 'Amelia', 'Harper', 'Evelyn']
};

// Professions
const professions = [
  'Software Engineer', 'Teacher', 'Nurse', 'Marketing Manager', 'Graphic Designer', 'Accountant', 'Chef', 
  'Writer', 'Doctor', 'Lawyer', 'Architect', 'Journalist', 'Musician', 'Photographer', 'Fitness Trainer',
  'Sales Representative', 'Financial Analyst', 'Social Worker', 'Veterinarian', 'Entrepreneur', 'Barista',
  'Mechanical Engineer', 'Interior Designer', 'Event Planner', 'Research Scientist', 'Dentist', 'Police Officer',
  'Pharmacist', 'Customer Service Representative', 'Real Estate Agent'
];

// Educational backgrounds
const education = [
  'High School Diploma', 'Associate\'s Degree', 'Bachelor\'s Degree in Business', 'Bachelor\'s Degree in Arts',
  'Bachelor\'s Degree in Science', 'Master\'s Degree in Engineering', 'Master\'s Degree in Education',
  'PhD in Physics', 'MBA', 'Medical Degree', 'Law Degree', 'No College Degree', 'Trade School',
  'Bachelor\'s Degree in Psychology', 'Master\'s Degree in Social Work', 'Bachelor\'s Degree in Computer Science',
  'Associate\'s Degree in Nursing', 'Bachelor\'s Degree in Communications', 'Some College', 'Self-Taught'
];

// Nationalities and cultural backgrounds
const nationalities = [
  { country: 'United States', background: 'American' },
  { country: 'Canada', background: 'Canadian' },
  { country: 'United Kingdom', background: 'British' },
  { country: 'Mexico', background: 'Mexican' },
  { country: 'India', background: 'Indian' },
  { country: 'China', background: 'Chinese' },
  { country: 'Japan', background: 'Japanese' },
  { country: 'Germany', background: 'German' },
  { country: 'France', background: 'French' },
  { country: 'Brazil', background: 'Brazilian' },
  { country: 'Nigeria', background: 'Nigerian' },
  { country: 'Australia', background: 'Australian' },
  { country: 'Italy', background: 'Italian' },
  { country: 'Spain', background: 'Spanish' },
  { country: 'South Korea', background: 'Korean' },
  { country: 'Russia', background: 'Russian' },
  { country: 'Ireland', background: 'Irish' },
  { country: 'Sweden', background: 'Swedish' },
  { country: 'Egypt', background: 'Egyptian' },
  { country: 'South Africa', background: 'South African' }
];

// Interests for nerdy personality
const nerdyInterests = [
  'video games', 'sci-fi movies', 'anime', 'board games', 'programming', 'fantasy novels',
  'comic books', 'D&D', 'astronomy', 'history', 'technology', 'robotics', 'AI', 'virtual reality',
  'classic literature', 'mathematics', 'physics', 'chess', 'mythology', 'debate'
];

// Physical attributes
const physicalAttributes = {
  hairColor: ['black', 'brown', 'blonde', 'red', 'dark brown', 'light brown', 'chestnut', 'auburn'],
  eyeColor: ['brown', 'blue', 'green', 'hazel', 'gray', 'amber'],
  height: ['short', 'average height', 'tall', 'very tall'],
  build: ['slender', 'athletic', 'average', 'muscular', 'curvy', 'stocky']
};

// Socioeconomic backgrounds
const socioeconomicBackgrounds = [
  'grew up in a working-class family', 
  'comes from a middle-class background',
  'was raised in an upper-middle-class household',
  'grew up in a wealthy family',
  'was raised in a single-parent household',
  'grew up in a small rural town',
  'was raised in the suburbs',
  'grew up in a big city',
  'immigrated to this country as a child',
  'comes from a family of academics',
  'grew up in a family business',
  'was raised by grandparents',
  'moved around a lot as a child',
  'grew up in poverty but worked hard to overcome it',
  'was raised in a close-knit community',
  'comes from a long line of military service'
];

// Helper function to get random item from array
const getRandomItem = (array) => array[Math.floor(Math.random() * array.length)];

// Generate random background story based on attributes
const generateBackground = (gender, nationality, socioeconomic, education, profession) => {
  const hobbies = [
    'hiking', 'reading', 'cooking', 'traveling', 'photography', 'painting',
    'playing music', 'dancing', 'gardening', 'meditation', 'yoga', 'running',
    'swimming', 'cycling', 'volunteering', 'collecting vinyl records',
    'watching documentaries', 'trying new restaurants', 'camping', 'kayaking'
  ];

  const hobby1 = getRandomItem(hobbies);
  let hobby2 = getRandomItem(hobbies);
  while (hobby2 === hobby1) {
    hobby2 = getRandomItem(hobbies);
  }

  const relationshipStatus = getRandomItem([
    'recently got out of a long-term relationship',
    'has been single for about a year',
    'has been focusing on career rather than dating',
    'has been dating casually but nothing serious',
    'is ready to find something meaningful',
    'took a break from dating to work on personal growth',
    'just moved to the area and looking to meet new people'
  ]);

  return `${gender === 'male' ? 'He' : 'She'} ${socioeconomic} and studied to earn ${education ? `a ${education.toLowerCase()}` : 'their degree'}. Currently working as a ${profession} in ${nationality.country}. In free time, enjoys ${hobby1} and ${hobby2}. ${relationshipStatus}.`;
};

// Main function to generate a character
export const generateCharacter = (personalityType, gender = 'female') => {
  const name = getRandomItem(names[gender]);
  const age = Math.floor(Math.random() * 15) + 25; // Age between 25-40
  const profession = getRandomItem(professions);
  const nationality = getRandomItem(nationalities);
  const educationLevel = getRandomItem(education);
  const socioeconomic = getRandomItem(socioeconomicBackgrounds);
  
  // Physical traits
  const hairColor = getRandomItem(physicalAttributes.hairColor);
  const eyeColor = getRandomItem(physicalAttributes.eyeColor);
  const height = getRandomItem(physicalAttributes.height);
  const build = getRandomItem(physicalAttributes.build);
  
  // Special traits for nerdy personality
  let interest1, interest2;
  if (personalityType.id === 'nerdy') {
    interest1 = getRandomItem(nerdyInterests);
    interest2 = getRandomItem(nerdyInterests.filter(i => i !== interest1));
  }
  
  // Generate background story
  const background = generateBackground(gender, nationality, socioeconomic, educationLevel, profession);
  
  // Compile character data
  const character = {
    name,
    gender,
    age,
    profession,
    nationality: nationality.background,
    country: nationality.country,
    education: educationLevel,
    socioeconomic,
    physical: {
      hairColor,
      eyeColor,
      height,
      build
    },
    background,
    interest1,
    interest2
  };
  
  return character;
};

// Generate an avatar URL from dicebear API based on character traits
export const generateAvatarUrl = (character, mood = 'neutral') => {
  // Map our moods to Dicebear emotions
  const moodMap = {
    happy: 'happy',
    excited: 'happy',
    sad: 'sad',
    neutral: 'neutral',
    bored: 'neutral',
    curious: 'surprised',
    angry: 'angry',
    suspicious: 'concerned',
    condescending: 'smirk'
  };

  const dicebearMood = moodMap[mood] || 'neutral';

  const isMale = character.gender === 'male';

  // 'lorelei' has female-presenting avatars; 'micah' has male-presenting ones.
  // Both styles support the mood/hairColor query params used below.
  const style = isMale ? 'micah' : 'lorelei';

  // Create seed from character name and mood to get consistent but different images
  const seed = `${character.name}-${character.age}-${dicebearMood}`;

  // Use hair color and other traits from character to customize the avatar
  // Map text hair colors to hex color codes as required by DiceBear API
  const hairColorMap = {
    'black': '000000',
    'brown': '6B4423',
    'blonde': 'FFF5E1',
    'red': 'A52A2A',
    'dark brown': '3B2F2F',
    'light brown': 'A17A74',
    'chestnut': '954535',
    'auburn': '922724'
  };

  const hairColor = character.physical.hairColor.replace(' ', '');
  // Use hex color code if available, otherwise don't include the parameter
  const hexColor = hairColorMap[hairColor] || hairColorMap[character.physical.hairColor];
  const hairColorParam = hexColor ? `&hairColor=${hexColor}` : '';

  // Create a simple hash of the character's name and age so the same
  // character always gets the same look
  const nameSum = character.name.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);

  let customParams;
  if (isMale) {
    // Give 'micah' male avatars a chance at facial hair, and few earrings
    customParams = `&facialHairProbability=50&earringsProbability=10`;
  } else {
    // Create an array of allowed hair variants for 'lorelei'
    const allowedHairVariants = [
      'variant10', 'variant11', 'variant12', 'variant13', 'variant14',
      'variant15', 'variant16', 'variant17', 'variant18', 'variant19',
      'variant20', 'variant21', 'variant24', 'variant29', 'variant30',
      'variant31', 'variant32', 'variant35', 'variant40', 'variant41',
      'variant42', 'variant43', 'variant45', 'variant46', 'variant48'
    ];

    // Use character attributes to deterministically select a hair variant
    const hairIndex = (nameSum + parseInt(character.age)) % allowedHairVariants.length;
    const selectedHairVariant = allowedHairVariants[hairIndex];

    // 1. Set beardProbability to 0 (no beards)
    // 2. Set earrings to 50% (earringsProbability=50)
    // 3. Use only specified hair variants with consistent selection
    customParams = `&beardProbability=0&earringsProbability=50&hair=${selectedHairVariant}`;
  }

  return `https://api.dicebear.com/7.x/${style}/svg?seed=${seed}&mood=${dicebearMood}${hairColorParam}${customParams}`;
};
